import { CheckIcon, MinusIcon } from '@heroicons/react/24/outline';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as React from 'react';

import { cn } from '@/lib/utils';

// A DOBOZ vizuális alakja EGY helyen: a valódi (Radix, interaktív) és a PRESENTATIONAL változat is ebből él —
// különben a kettő a következő token-hangolásnál elcsúszik.
// Checkbox: 16px, 4px rádiusz, erős keret; bejelölve PRIMARY (near-black — dark módban invertál).
const BOX =
  'border-border-strong bg-card ease-atlas h-4 w-4 shrink-0 rounded-sm border transition-colors duration-150';
const BOX_CHECKED = 'border-primary bg-primary text-primary-foreground';

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      BOX,
      'peer focus-visible:outline-none',
      'focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50',
      // A KÖZTES („indeterminate") állapot UGYANAZT a kitöltött dobozt kapja, csak MÍNUSZ jellel: enélkül a
      // Radix ugyan rendereli az indikátort, de a doboz a kitöltés nélkül ÜRESNEK látszott — a felhasználó
      // szemében „nem jelölte ki", holott ki volt jelölve (a számla-lista mind-jelölője ilyen volt).
      'data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground',
      'data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground',
      className,
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator className={cn('flex items-center justify-center text-current')}>
      {props.checked === 'indeterminate' ? (
        <MinusIcon className="h-3.5 w-3.5" />
      ) : (
        <CheckIcon className="h-3.5 w-3.5" />
      )}
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

/**
 * PRESENTATIONAL checkbox — csak a doboz KINÉZETE, `<span>`-ként, semmilyen interakció és szemantika nélkül.
 *
 * MIÉRT LÉTEZIK: a több-választós pickerekben a KATTINTHATÓ elem a teljes sor (`<button role="option">`), a
 * checkbox pedig csak jelző. Ha oda a Radix `Checkbox` kerül, egy `<button>` ül egy `<button>`-ben — érvénytelen
 * DOM, és a Radix Indicator `Presence`-e ezen a fán VÉGTELEN layout-effect ciklusba futott („Maximum update
 * depth exceeded", mérve résztvevő-kijelöléskor). Ez a `<span>` ugyanazt a doboz-stílust adja,
 * de nem interaktív elem, tehát a sor-gomb marad az EGYETLEN vezérlő (a11y: a `role="option"` +
 * `aria-selected` hordozza az állapotot).
 */
export function CheckboxVisual({
  checked,
  indeterminate = false,
  className,
}: {
  checked: boolean;
  /** KÖZTES állapot (pl. egy csoport tagjainak egy része kijelölve) — a kitöltött doboz mínusz jellel, mint a Radix-változaté */
  indeterminate?: boolean;
  className?: string;
}): JSX.Element {
  const filled = checked || indeterminate;
  return (
    <span
      aria-hidden
      className={cn(
        BOX,
        'inline-flex items-center justify-center',
        filled && BOX_CHECKED,
        className,
      )}
    >
      {checked ? (
        <CheckIcon className="h-3.5 w-3.5" />
      ) : (
        indeterminate && <MinusIcon className="h-3.5 w-3.5" />
      )}
    </span>
  );
}

export { Checkbox };
